import React, {
  Children,
  createElement,
  forwardRef,
  memo,
  useImperativeHandle,
  useEffect,
} from 'react';
import { Box, type SxProps } from '@mui/material';
import { Controller, useForm, type UseFormProps } from 'react-hook-form';
import { isNonNullish, keys, pickBy, pipe } from 'remeda';

type FormChild = React.ReactElement<any> | false;

type Props = {
  sx?: SxProps;
  resolver: UseFormProps['resolver'];
  defaultValues: UseFormProps['defaultValues'];
  children: FormChild | FormChild[];
  onChange?: (values: any) => void;
  onSubmit: (data: any) => void;
};

export type FormElement = {
  submitForm: () => void;
  resetForm: () => void;
} & HTMLFormElement;

const Form = forwardRef(
  (
    { sx, resolver, defaultValues, children, onChange, onSubmit }: Props,
    ref,
  ) => {
    const {
      handleSubmit,
      control,
      formState: { errors, touchedFields },
      watch,
      reset,
    } = useForm({ resolver, defaultValues });

    useEffect(() => {
      if (onChange) {
        const subscription = watch(onChange);
        return () => subscription.unsubscribe();
      }
      return () => {};
    }, [watch, onChange]);

    useImperativeHandle(ref, () => ({
      submitForm: () => handleSubmit(onSubmit)(),
      resetForm: () => reset(),
      touchedFields: pipe(touchedFields, pickBy(isNonNullish), keys),
    }));

    const addFormProps = (child: FormChild): any => {
      if (!child) return null;

      if (child.type === React.Fragment) {
        return React.Children.map(child.props.children, addFormProps);
      }

      if (child?.props?.children) {
        return createElement(
          child.type,
          child.props,
          React.Children.map(child.props.children, addFormProps),
        );
      }

      const name = child?.props?.name as string;

      const error = !!errors?.[name];
      const helperText = errors?.[name]?.message;

      return name ? (
        <Controller
          key={name}
          control={control}
          name={name}
          render={({ field }) =>
            createElement(child.type, {
              ...{
                ...child.props,
                field,
                error,
                helperText,
              },
            })
          }
        />
      ) : (
        child
      );
    };

    return (
      <Box ref={ref} sx={sx} component="form" onSubmit={handleSubmit(onSubmit)}>
        {Children.map(children, addFormProps)}
      </Box>
    );
  },
);

export default memo(Form);
